import React from "react";
import { SectionTitle, Panel, Pill } from "@/components/croc/ui";
import { CONTRACTS, CHAIN } from "@/contracts/croc";
import { shortAddr } from "@/lib/format";
import { BookOpen, Sigma, Calculator, Shield, Coins, ExternalLink } from "lucide-react";

const F = ({ children }) => (
  <code className="font-num text-sm bg-[#090E0B] border border-white/10 rounded-lg px-3 py-2 block text-[#34D399] my-2 overflow-x-auto">{children}</code>
);
const H = ({ children }) => <h3 className="font-display font-bold text-white text-lg mt-6 mb-2">{children}</h3>;
const P = ({ children }) => <p className="text-sm text-muted-foreground leading-relaxed">{children}</p>;

export default function Docs() {
  return (
    <div className="max-w-4xl">
      <SectionTitle
        eyebrow="Whitepaper · Tokenomics · Math"
        title="Protocol Docs"
        subtitle="The economics, formulas and sustainability model behind CROC PROTOCOL."
        right={<Pill tone="gold"><BookOpen className="w-3 h-3" /> Investor Brief</Pill>}
        testid="docs-title"
      />

      <Panel data-testid="docs-guardrails" className="mb-6">
        <div className="flex items-center gap-2 mb-3"><Shield className="w-4 h-4 gold-text" /><span className="font-display font-bold text-white">Non-negotiable guardrails</span></div>
        <ul className="space-y-2 text-sm text-muted-foreground">
          <li>• <span className="text-white">Hard cap:</span> 1,000,000,000 CROC. No infinite mint — rewards drawn from a pre-allocated treasury bucket.</li>
          <li>• <span className="text-white">Variable yield:</span> daily rate floats with realized revenue; admin sets a rate ≤ 0.5%/day ceiling.</li>
          <li>• <span className="text-white">Revenue-funded only:</span> referral, rank, level and pool rewards paid from the Revenue Pool. If revenue &lt; obligations → pro-rata scale-down. Never from principal.</li>
          <li>• <span className="text-white">Legal:</span> revenue-share + multi-level referral may create securities/MLM exposure (US SEC/FTC, EU MiCA, Asia). Obtain counsel + consider KYC/AML before mainnet.</li>
        </ul>
      </Panel>

      <Panel data-testid="docs-formulas" className="mb-6">
        <div className="flex items-center gap-2 mb-1"><Sigma className="w-4 h-4 emerald-text" /><span className="font-display font-bold text-white">Core reward formulas</span></div>
        <H>Effective daily rate</H>
        <F>effective_daily = min(base_daily × tier_multiplier, 0.5%)</F>
        <P>Tier multipliers: 24h = 1.00×, 90d = 1.10×, 180d = 1.25×, 360d = 1.50×, 720d = 1.75×.</P>
        <H>Daily & total reward</H>
        <F>daily_reward = principal × effective_daily</F>
        <F>total_reward = min(principal × effective_daily × days_elapsed, principal × tier_max)</F>
        <H>APR / APY</H>
        <F>APR = effective_daily × 365 &nbsp;|&nbsp; APY = (1 + effective_daily)^365 − 1</F>
        <H>Direct sponsor bonus (5%, excludes 24h tier)</H>
        <F>sponsor_bonus = stake_amount × 0.05 &nbsp;(paid from Revenue Pool)</F>
        <H>Override income (21 levels, reward-on-reward)</H>
        <F>override_income(L) = downline_reward × level_pct(L), if directs ≥ req_directs(L)</F>
        <H>Share value</H>
        <F>share_value = (5% × turnover) / total_shares &nbsp;|&nbsp; shares = floor(self_topup / 2,500)</F>
        <H>Pool distribution</H>
        <F>distribute_today = pool_total × 30% &nbsp;|&nbsp; carryforward = pool_total × 70%</F>
        <F>winners = [50%, 30%, 20%] × distribute_today</F>
        <H>Withdrawal (10% fee, burn on sell)</H>
        <F>net = gross × 0.90 &nbsp;|&nbsp; burned_CROC = net / live_price</F>
      </Panel>

      <Panel data-testid="docs-examples" className="mb-6">
        <div className="flex items-center gap-2 mb-3"><Calculator className="w-4 h-4 gold-text" /><span className="font-display font-bold text-white">Worked examples</span></div>
        <div className="space-y-4 text-sm">
          <div className="rounded-xl border border-white/10 bg-[#090E0B] p-4">
            <div className="text-white font-medium mb-1">Example 1 — 360d Apex Predator, $5,000</div>
            <P>Fixed 0.40%/day → daily = $20 → 30-day = $600. Rewards accrue until the 400% cap ($20,000) or the 360-day lock ends, whichever comes first — then you can reclaim your $5,000 principal as CROC at the live rate.</P>
          </div>
          <div className="rounded-xl border border-white/10 bg-[#090E0B] p-4">
            <div className="text-white font-medium mb-1">Example 2 — Direct sponsor bonus</div>
            <P>Referral stakes $2,000 in the 180d tier → sponsor receives $100 (5%) from the Revenue Pool. A 24h-tier stake pays no sponsor bonus.</P>
          </div>
          <div className="rounded-xl border border-white/10 bg-[#090E0B] p-4">
            <div className="text-white font-medium mb-1">Example 3 — Shares</div>
            <P>Self top-up $12,500 → floor(12,500 / 2,500) = 5 shares. If share pool = $50,000 over 10,000 shares → share_value $5.00 → your value $25.00.</P>
          </div>
          <div className="rounded-xl border border-white/10 bg-[#090E0B] p-4">
            <div className="text-white font-medium mb-1">Example 4 — Croc Pool winners</div>
            <P>Croc Pool total $20,000 → 30% distributed today = $6,000 → King Croc (#1) takes 20% ($4,000), Baby Croc (#2) takes 10% ($2,000); the remaining $14,000 (70%) carries forward.</P>
          </div>
          <div className="rounded-xl border border-white/10 bg-[#090E0B] p-4">
            <div className="text-white font-medium mb-1">Example 5 — Withdrawal</div>
            <P>Available $1,000, withdraw 50% → gross $500, fee $50, net $450; at $0.048/CROC ≈ 9,375 CROC burned.</P>
          </div>
        </div>
      </Panel>

      <Panel data-testid="docs-sustainability" className="mb-6">
        <div className="flex items-center gap-2 mb-1"><Coins className="w-4 h-4 emerald-text" /><span className="font-display font-bold text-white">Sustainability model</span></div>
        <F>coverage_ratio = realized_revenue / daily_obligations</F>
        <P className="mt-2">
          When coverage ≥ 1.0×, all rewards pay in full. When coverage &lt; 1.0×, the auto-scaler pays distributions pro-rata
          (each recipient receives coverage_ratio × their nominal reward) so payouts never touch user principal or dilute the fixed supply.
          Bank-run defense: withdrawal spikes are throttled by the Revenue Pool balance and the same pro-rata scaler.
        </P>
      </Panel>

      <Panel data-testid="docs-contracts">
        <div className="font-display font-bold text-white mb-3">Deployed contracts · {CHAIN.name}</div>
        <div className="grid sm:grid-cols-3 gap-3">
          {Object.entries(CONTRACTS).map(([name, addr]) => (
            <a key={name} href={`${CHAIN.explorer}/address/${addr}`} target="_blank" rel="noreferrer"
              className="rounded-xl border border-white/10 bg-[#090E0B] p-3 hover:border-[#1CFD4C]/50 transition-colors">
              <div className="flex items-center justify-between"><span className="text-sm text-white">{name}</span><ExternalLink className="w-3.5 h-3.5 text-muted-foreground" /></div>
              <div className="text-[11px] font-num text-muted-foreground mt-1">{shortAddr(addr)}</div>
            </a>
          ))}
        </div>
      </Panel>
    </div>
  );
}
